import { IPanelStyles, IStackStyles, IStackTokens, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, StackItem, TextField } from "@fluentui/react";
import React from "react";
import generalModule from "../../utilities/GeneralModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { useSelector } from "react-redux";
import { RootState } from "../../src/taskpane/redux";
import { ComponentTypes } from "../../src/taskpane/config/config";
import authModule from "../../utilities/AuthModule";
import { createLink, getShortcutLink } from "../../src/taskpane/services/SharePointRestServices";
import { isSharePointSharingUrl, isValidHttpUrl } from "../../src/taskpane/components/helpers/UrlHelper";
import { getDriveItemBySharingUrl } from "../../src/taskpane/services/GraphService";
import { CacheHelper } from "../../utilities/CacheHelper";
import { logger } from "../../utilities/logger";
import { AxiosError } from "axios";
import { tryDecodeUriComponent } from "../../utilities/EncodeDecode";
import { contentManager } from "../../src/taskpane/config/constants";

const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingTop: 20,
        paddingLeft: 15,
        paddingRight: 15
    }
}

const linkStyles: IStackStyles = {
    root: {
        margin: 5,
        minWidth: 0
    }
};

const linkTokens: IStackTokens = {
    childrenGap: 10
};

export interface ICMNewLinkPanelProps {
    panelIsOpen: boolean;
    closePanel: () => void;
    reloadGrid: () => void;
    serverRelPath: string;
}

export const CMNewLinkPanel = ({ panelIsOpen, closePanel, reloadGrid, serverRelPath }: ICMNewLinkPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const [link, setLink] = React.useState("");
    const [showFileName, setShowFileName] = React.useState(false);
    const [fileName, setFileName] = React.useState("");
    const [isCreatingLink, setIsCreatingLink] = React.useState<boolean>(false);
    const [createLinkErrorMessage, setCreateLinkErrorMessage] = React.useState<string | null>();

    const isInOneDrive = React.useRef(false);

    React.useEffect(() => {
        if (currentDirectory) {
            isInOneDrive.current = currentDirectory.type == ComponentTypes.OneDrive || currentDirectory.type == ComponentTypes.OneDriveSubFolder;
        }
    }, [currentDirectory]);

    const locationOrigin: string = React.useMemo(() => {
        let origin: string
        try {
            origin = new URL(currentDirectory.webUrl.trim()).origin;
            if (origin.endsWith("/")) {
                origin = origin.slice(0, -1);
            }   
        } catch (error) {
            // Ignore
        }
        return origin ?? "";
    }, [currentDirectory]);

    const onLinkChanged = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (createLinkErrorMessage) {
            setCreateLinkErrorMessage(null);
        }
        try {
            let trimmedValue: string = newValue?.trim() ?? "";
            if (trimmedValue.length > 0) {
                setShowFileName(true);

                let useHostAsFileName: boolean = true;
                let formattedValue: string = trimmedValue.toLowerCase();

                if (formattedValue.startsWith("h")
                    || formattedValue.startsWith("ht")
                    || formattedValue.startsWith("htt")
                    || formattedValue.startsWith("http")

                    || formattedValue.startsWith("http:")
                    || formattedValue.startsWith("http:/")
                    || formattedValue.startsWith("http://")

                    || formattedValue.startsWith("https")
                    || formattedValue.startsWith("https:")
                    || formattedValue.startsWith("https:/")
                    || formattedValue.startsWith("https://")) {

                    if (formattedValue.startsWith("https://")) {
                        if (trimmedValue.startsWith(`${process.env.contentManagerUrl}?${contentManager.params.preloadURL}=`)) {
                            try {
                                const absoluteUrl = new URL(trimmedValue);
                                const preloadURL: string = absoluteUrl.searchParams.get(contentManager.params.preloadURL);
                                if (preloadURL) {
                                    trimmedValue = tryDecodeUriComponent(preloadURL);
                                }
                            } catch (error) {
                                // ignore
                            }
                        }

                        if (isSharePointSharingUrl(trimmedValue)) {
                            useHostAsFileName = false;
                            (async () => {
                                const results = await getDriveItemBySharingUrl(trimmedValue);
                                if (results?.name) {
                                    setLink(trimmedValue);
                                    setFileName(results.name);
                                }
                                else {
                                    setLink(formattedValue);
                                    if (!fileName) {
                                        setFileName(new URL(formattedValue).hostname);
                                    }
                                }
                            })();
                        }
                        else if (formattedValue.startsWith(locationOrigin.trim().toLowerCase())) {
                            try {
                                const linkUrlPathParts: string[] = new URL(formattedValue).pathname.split("/");
                                if (linkUrlPathParts.length > 0) {
                                    const lastPart: string = linkUrlPathParts[linkUrlPathParts.length - 1];
                                    if (lastPart) {
                                        useHostAsFileName = false;
                                        setFileName(tryDecodeUriComponent(lastPart)?.trim());
                                    }
                                }
                            } catch (error) {
                                // ignore
                            }
                        }
                    }

                    setLink(trimmedValue);
                }
                else {
                    formattedValue = "https://" + formattedValue;
                    setLink(formattedValue);
                }

                if (formattedValue && useHostAsFileName) {
                    let host: string = new URL(formattedValue).hostname;
                    if (host.length > 0) {
                        setFileName(host);
                    }
                }
            }
            else {
                setLink("");
                setShowFileName(false);
                setFileName("");
            }
        } catch (error) {
            // 
        }
    };

    const onFileNameChanged = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setFileName(newValue);
        setCreateLinkErrorMessage(null);
    };

    const createLinkItem = async () => {
        setIsCreatingLink(true);

        try {
            const accessToken = !isInOneDrive.current
                ? await authModule.getSharePointAccessToken()
                : await authModule.getOneDriveAccessToken();

            const payload = `[InternetShortcut]\r\nURL=${link}`;

            const createdUrlFile: any = await createLink(accessToken, currentDirectory?.webUrl, serverRelPath, fileName + ".url", payload);
            if (createdUrlFile?.ServerRelativeUrl) {
                const fullPath: string = locationOrigin + createdUrlFile.ServerRelativeUrl;
                const shortcutLink = await getShortcutLink(accessToken, currentDirectory.webUrl, fullPath);
                if (shortcutLink?.uniqueId && shortcutLink?.url) {
                    let shortcutLinkUrl: string;
                    if (shortcutLink.url.trim().toLowerCase().startsWith(locationOrigin.trim().toLowerCase())) {
                        const shortcutLinkUri: URL = new URL(shortcutLink.url);
                        shortcutLinkUrl = shortcutLinkUri.pathname + shortcutLinkUri.search;
                    }
                    else {
                        shortcutLinkUrl = shortcutLink.url;
                    }
                    CacheHelper.set(`shortcutLink_${createdUrlFile.UniqueId?.toLowerCase()}`, shortcutLinkUrl);
                    reloadGrid();
                    closePanel();
                }
            }
        } catch (error) {
            logger(error, "CMNewLinkPanel (createLinkItem)");
            if ((error as AxiosError) && error.response?.data?.error) {
                if (error.response.data.error.code?.includes("2130575257")) {
                    setCreateLinkErrorMessage(languageStrings.FileSameName);
                }
                else {
                    setCreateLinkErrorMessage(error.response.data.error?.message?.value ?? languageStrings.ErrorMessageGeneric);
                }
            }
            else {
                setCreateLinkErrorMessage(error.message ?? languageStrings.ErrorMessageGeneric);
            }
        }
        setIsCreatingLink(false);
    };

    const onRenderFooterContent = (): JSX.Element => (
        <Stack horizontal horizontalAlign="end">
            <PrimaryButton disabled={!fileName?.length || !isValidHttpUrl(link) || createLinkErrorMessage?.length > 0} text={languageStrings.Create} allowDisabledFocus onClick={() => createLinkItem()}>
                {isCreatingLink && <Spinner size={SpinnerSize.small} styles={{ root: { marginLeft: 5 } }} />}
            </PrimaryButton>
        </Stack>
    );

    return (
        <Panel
            isOpen={panelIsOpen}
            headerText="Create link to"
            onDismiss={closePanel}
            isLightDismiss
            closeButtonAriaLabel="Close"
            customWidth={!generalModule.isInConentMangerMode() ? "300px" : "350px"}
            type={PanelType.custom}
            styles={panelStyles}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}>
            <Stack tokens={linkTokens} styles={linkStyles}>
                <StackItem>
                    <TextField
                        autoFocus
                        value={link}
                        onChange={onLinkChanged}
                        className="xb-sp-input"
                        placeholder={languageStrings.EnterLinkWebPageFile}
                    />
                </StackItem>
                <StackItem>
                    {showFileName && <TextField errorMessage={createLinkErrorMessage?.length > 0 ? createLinkErrorMessage : undefined} label={languageStrings.FileName} className="xb-sp-input" value={fileName} onChange={onFileNameChanged} suffix=".url"></TextField>}
                </StackItem>
            </Stack>
        </Panel>
    )
};